import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { capitalize, getUniqueHtmlId } from '../../../../utils/util/formatter';
import { createName, formatFilterList } from '../../../EngagementSurvey/utils';
import {
  GetColorFunc,
  GetValueFunc,
  HeatmapRowData,
  HeatmapState,
} from './types';

const HeatmapRow_propTypes = {
  firstColumnIsTitle: PropTypes.bool,
  formatPopover: PropTypes.func,
  popoverChart: PropTypes.func,
  isMultipleChoice: PropTypes.bool,
  unit: PropTypes.string,
};

type Props = PropTypes.InferProps<typeof HeatmapRow_propTypes> & {
  data: HeatmapRowData;
  columns: string[];
  colorScale: GetColorFunc;
  index: number;
  heatmapState: HeatmapState;
  isUnfrozen?: boolean;
  hasFactorHeaders?: boolean;
  formatValue: GetValueFunc;
};

export const MAX_COL_WIDTH = '200px';

const HeatmapRow: React.FC<Props> = (props) => {
  const { formatMessage, formatList } = useIntl();
  const isFactorHeader = props.data?._isHeader;

  const {
    isUnfrozen,
    index: rowIndex,
    heatmapState: propsHeatmapState,
  } = props;

  return (
    <Row
      className={`gx-0 heatmap-row ` + (isFactorHeader ? 'fw-bold' : '')}
      style={{ height: isFactorHeader ? '5rem' : '4rem' }}
    >
      {props.columns.map((c, ci) => {
        const isHeader = props.firstColumnIsTitle && !ci;

        const data = props.data?.[c]?.responses;
        const rawValue = data?.value || data;
        let popoverData;

        if (props.isMultipleChoice) {
          popoverData = { total: data?.count };
        } else if (props.formatPopover) {
          popoverData = props.formatPopover(rawValue);
        }

        const participationRate =
          Math.round(
            (100 * (popoverData?.total || 1)) / (props.data?.[c]?.eligible || 1)
          ) + '%';

        const columnName =
          c === '_others'
            ? formatMessage({
                id: 'app.views.widgets.charts.heatmap.heatmap_row.others',
                defaultMessage: 'Others',
              })
            : capitalize(c);

        const isAll = c === 'all';
        const isOthers = c === '_others';
        let popoverColumnAdditionalData = '';
        if (isAll && propsHeatmapState?.segment) {
          const excluded_values =
            props.data?.['all']?.['excluded_filter_values']?.[
              propsHeatmapState.segment.id
            ] ?? [];
          if (excluded_values.length > 0) {
            popoverColumnAdditionalData = formatMessage(
              {
                id: 'app.views.widgets.charts.heatmap.heatmap_row.excluded_values_info',
                defaultMessage: '(Excludes responses from {excluded_values})',
              },
              {
                excluded_values: formatList(
                  formatFilterList(excluded_values, formatMessage),
                  {
                    type: 'conjunction',
                  }
                ),
              }
            );
          }
        } else if (isOthers && propsHeatmapState?.segment) {
          const included_values =
            props.data?.['_others']?.['included_filter_values']?.[
              propsHeatmapState.segment.id
            ] ?? [];
          if (included_values.length > 0) {
            popoverColumnAdditionalData = formatMessage(
              {
                id: 'app.views.widgets.charts.heatmap.heatmap_row.included_values_info',
                defaultMessage: '(Includes responses from {included_values})',
              },
              {
                included_values: formatList(
                  formatFilterList(included_values, formatMessage),
                  {
                    type: 'conjunction',
                  }
                ),
              }
            );
          }
        }

        let value;

        if (isHeader) {
          value = createName(
            formatMessage,
            props.data?.[c],
            props.data?.count,
            'likert-' + props.index,
            isFactorHeader ? 'fs-4' : 'fs-5',
            50,
            isFactorHeader
          );
        } else {
          value = props.formatValue(rawValue);
        }

        let displayValue = value;
        if (!isHeader && props.isMultipleChoice && value !== '-') {
          displayValue += '%';
        }
        if (props.unit && !isHeader && value !== '-') {
          displayValue += ` ${props.unit}`;
        }

        const uniqueId = getUniqueHtmlId('heatcell');

        const colorValue = props.colorScale(
          value,
          props.data,
          rowIndex,
          ci,
          propsHeatmapState
        );

        const style = isUnfrozen
          ? {
              backgroundColor: colorValue?.background,
              color: colorValue?.text,
              maxWidth: MAX_COL_WIDTH,
            }
          : {
              color: colorValue?.text,
            };

        return (
          <Col
            key={ci}
            className={
              `heatmap-row-cell border-bottom ` +
              (isHeader ? 'col-10 px-3 border-start ' : 'col-2 ') +
              (props.hasFactorHeaders && !isFactorHeader && isHeader
                ? 'ps-5'
                : '') +
              (props.index ? '' : 'border-top border-light ') +
              (props.index && isFactorHeader
                ? 'border-top border-top-3 '
                : '') +
              (isFactorHeader ? 'fs-4 ' : '')
            }
            style={style}
          >
            <Row
              className={
                `h-100 align-content-center justify-content-center ` +
                (isHeader ? '' : 'text-center ') +
                (props.firstColumnIsTitle && ci ? ' fw-bold' : '')
              }
            >
              <span id={uniqueId} className="py-2">
                {!isUnfrozen && ci ? (
                  <Row>
                    <Col className="col-5 m-0 p-0 text-end">
                      <div
                        style={{
                          display: 'inline-block',
                          background: colorValue?.background,
                          borderRadius: '50%',
                          width: '10px',
                          height: '10px',
                          marginRight: '8px',
                          minWidth: '10px',
                          minHeight: '10px',
                        }}
                      />
                    </Col>
                    <Col className="col-7 m-0 p-0 text-start">
                      {displayValue}
                    </Col>
                  </Row>
                ) : (
                  displayValue
                )}
              </span>
            </Row>
            {!isHeader && displayValue !== '-' && (
              <UncontrolledPopover
                className="popover-variable-lg"
                placement="top"
                trigger="hover click"
                target={uniqueId}
              >
                <>
                  <div className="fw-bold mb-3">{props.data?._name}</div>
                  <div className="d-flex justify-content-between">
                    <span className="me-4">
                      <FormattedMessage
                        id="app.views.widgets.charts.heatmap.heatmap_row.segment"
                        defaultMessage="{segment}:"
                        values={{
                          segment: capitalize(props.data?._segment),
                        }}
                      />
                    </span>{' '}
                    <span className="fw-bold">
                      {columnName} {popoverColumnAdditionalData}
                    </span>
                  </div>
                  {props.isMultipleChoice && (
                    <div className="d-flex justify-content-between">
                      <span className="me-2">
                        <FormattedMessage
                          id="app.views.widgets.charts.heatmap.heatmap_row.choice"
                          defaultMessage="Choice:"
                        />
                      </span>{' '}
                      <span className="fw-bold">{props.data?._choice}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between">
                    <span>
                      <FormattedMessage
                        id="app.views.widgets.charts.heatmap.heatmap_row.responses"
                        defaultMessage="Responses:"
                      />
                    </span>{' '}
                    <span className="fw-bold">
                      {props.isMultipleChoice && <>{popoverData.total}</>}
                      {!props.isMultipleChoice && (
                        <span className="fw-normal">
                          <FormattedMessage
                            id="app.views.widgets.charts.heatmap.heatmap_row.total_of_eligible"
                            defaultMessage="{total} of {eligible}"
                            values={{
                              total: popoverData.total,
                              eligible: props.data?.[c]?.eligible,
                            }}
                          />
                        </span>
                      )}
                    </span>
                  </div>
                  {!props.isMultipleChoice && (
                    <div className="d-flex justify-content-between">
                      <span className="me-2">
                        <FormattedMessage
                          id="app.views.widgets.charts.heatmap.heatmap_row.participation_rate"
                          defaultMessage="Participation rate: "
                        />
                      </span>{' '}
                      <span className="fw-bold">{participationRate}</span>
                    </div>
                  )}
                  {!props.isMultipleChoice &&
                    props.popoverChart &&
                    props.popoverChart(uniqueId, popoverData)}
                </>
              </UncontrolledPopover>
            )}
          </Col>
        );
      })}
    </Row>
  );
};

export default React.memo(HeatmapRow);
